import { useState } from "react";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { CountSpinner } from "common/components/ui/CountSpinner";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePromotions from "common/datahooks/usePromotions";
import {
  getLifetimeLicenseSource,
  getLifetimeLicenseSourceCount,
} from "common/helpers/workspaceLicenseCounts";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";

import PlanStatCard from "../PlanStatCard";
import LicenseAccounts from "./LicenseAccounts";

export default function AllocateLtdSeats() {
  const [isOpen, setIsOpen] = useState(false);

  const {
    id: workspaceId,
    lifetime_license_count,
    licenses,
  } = useSelectedWorkspaceContext();
  const {
    ltdPromotion,
    appSumoPromotion,
    isLoadingPromotions,
    lifetimeUnassignedLicenseCount,
  } = usePromotions();
  const hasLifetimePromotion = !!ltdPromotion || !!appSumoPromotion;

  const { outreachLicensedAccounts } = useLicensedAccounts(workspaceId);
  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();

  const [seatCount, setSeatCount] = useState(lifetime_license_count);

  const assignedCount = outreachLicensedAccounts.length;
  const availableInPool =
    lifetimeUnassignedLicenseCount + (lifetime_license_count - seatCount);

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (open) {
      setSeatCount(lifetime_license_count);
    }
  }

  async function onSubmit() {
    const source = getLifetimeLicenseSource(
      ltdPromotion,
      appSumoPromotion,
      lifetime_license_count,
      seatCount,
      licenses,
    );
    if (!source || seatCount === lifetime_license_count) {
      return;
    }

    const count = getLifetimeLicenseSourceCount(
      licenses,
      source,
      lifetime_license_count,
      seatCount,
    );

    await setLifetimeLicenseCount({
      workspaceId,
      count,
      source,
      accountsToCancel: [],
    });
    setIsOpen(false);
  }

  if (!hasLifetimePromotion) {
    return null;
  }

  const isUpdateDisabled =
    seatCount === lifetime_license_count ||
    seatCount < assignedCount ||
    isSettingLifetimeLicenseCount;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="ml-auto" size="sm" variant="secondary-purple">
          Allocate seats
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader title="Allocate seats" />

        <DialogBody>
          <div className="mt-6 flex justify-center">
            <CountSpinner
              variant="lg"
              value={seatCount}
              setValue={setSeatCount}
              minValue={assignedCount}
              maxValue={lifetime_license_count + lifetimeUnassignedLicenseCount}
              minValueTooltipText="You must unassign all seats before you can remove them from this workspace."
              maxValueTooltipText="No more seats available"
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <PlanStatCard title="Assigned">
              <Tag variant="tertiary" size="md" className="w-fit">
                {assignedCount}
                <span className="text-black-500">/ {seatCount}</span>
              </Tag>
              <LicenseAccounts accounts={outreachLicensedAccounts} />
            </PlanStatCard>

            <PlanStatCard title="Available in pool">
              {isLoadingPromotions ? (
                <Skeleton className="h-6 w-10" />
              ) : (
                <Tag variant="success" size="md" className="w-fit">
                  {availableInPool}
                </Tag>
              )}
            </PlanStatCard>
          </div>

          {seatCount < assignedCount && (
            <Alert variant="warning" className="mt-4">
              Cannot remove seats assigned to accounts. Unassign seats to
              continue
            </Alert>
          )}
        </DialogBody>

        <DialogFooter className="justify-end">
          <DialogClose asChild>
            <Button variant="secondary-black">Cancel</Button>
          </DialogClose>

          <Button
            isLoading={isSettingLifetimeLicenseCount}
            disabled={isUpdateDisabled}
            onClick={onSubmit}
          >
            Update seats
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
